// src/components/DonationManagement.jsx
// Complete Donation Management with Back Button - 4-Color Theme
// ONLY these colors + white are used anywhere in this file:
// #0D47A1 (primary dark blue), #2196F3 (primary blue), 
// #90CAF9 (light blue), #E3F2FD (very light blue)

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Building, Users, Plus, Search, X, Eye, Edit, Trash2, CheckCircle, Clock, Gift, Gift as GiftIcon, ArrowDownCircle, ArrowUpCircle, ArrowRightLeft, AlertTriangle, Wifi, WifiOff, ArrowLeft, ArrowRight, User, Gem, Building2, Landmark, Lock, GraduationCap, HardHat, Monitor, Package, Tent, Wrench, BookOpen, CircleDot, School, Bus, Handshake, RotateCw, CheckCircle2, } from "lucide-react";

// ─── UTILITY COMPONENTS ───────────────────────────────────────────────────────

function GlassCard({
  children,
  className = "",
  hover = true
}) {
  return <div className={`glass-panel rounded-2xl shadow-lg shadow-[#0D47A1]/20 ${hover ? "card-hover" : ""} ${className}`}>
      {children}
    </div>;
}
function StatusBadge({
  status
}) {
  const map = {
    completed: "bg-[#0D47A1] text-white",
    pending: "bg-[#90CAF9] text-[#0D47A1]",
    cancelled: "bg-white text-[#0D47A1]",
    received: "bg-[#2196F3] text-white",
    approved: "bg-[#0D47A1] text-white"
  };
  const labels = {
    completed: "Completed",
    pending: "Pending",
    cancelled: "Cancelled",
    received: "Received",
    approved: "Approved"
  };
  return <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm ${map[status] || "bg-white text-[#0D47A1]"}`}>
      {labels[status] || status}
    </span>;
}
function DirectionBadge({
  direction
}) {
  if (direction === "given") {
    return <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm bg-[#0D47A1] text-white inline-flex items-center gap-1">
        <ArrowUpCircle size={11} /> School <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /> Trust
      </span>;
  }
  return <span className="text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider shadow-sm bg-[#90CAF9] text-[#0D47A1] inline-flex items-center gap-1">
      <ArrowDownCircle size={11} /> Received by School
    </span>;
}
function PartyTypeBadge({
  type
}) {
  const labels = {
    individual: "Individual",
    corporate: "Corporate",
    trust: " Trust/NGO",
    anonymous: "Anonymous",
    alumni: "Alumni",
    government: " Government",
    billionaire: "Philanthropist"
  };
  return <span className="text-[10px] font-medium px-2.5 py-1 rounded-full shadow-sm bg-[#E3F2FD] text-[#0D47A1]">
      {labels[type] || type}
    </span>;
}
function PurposeBadge({
  purpose
}) {
  const labels = {
    infrastructure: " Infrastructure",
    building: " Building",
    scholarship: "Scholarship",
    equipment: "Equipment",
    general: "General",
    event: "Event",
    maintenance: "Maintenance",
    library: "Library",
    sports: "Sports",
    technology: " Technology",
    staff: "Staff Welfare",
    transport: "Transport",
    disaster_relief: "🆘 Disaster Relief",
    community: "Community Support"
  };
  return <span className="text-[10px] font-medium px-2.5 py-1 rounded-full shadow-sm bg-[#2196F3] text-white">
      {labels[purpose] || purpose}
    </span>;
}
function AnimatedCounter({
  value,
  duration = 1000,
  prefix = "",
  suffix = ""
}) {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  useEffect(() => {
    if (hasAnimated) {
      setCount(value);
      return;
    }
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * value));
      if (progress < 1) requestAnimationFrame(animate);else {
        setCount(value);
        setHasAnimated(true);
      }
    };
    requestAnimationFrame(animate);
  }, [value, duration, hasAnimated]);
  return <span>{prefix}{count.toLocaleString()}{suffix}</span>;
}

// ─── FORM MODAL ───────────────────────────────────────────────────────────────

function DonationFormModal({
  isOpen,
  onClose,
  onSave,
  editingDonation,
  saving,
  formError
}) {
  const emptyForm = {
    direction: "received",
    partyName: "",
    partyType: "individual",
    partyEmail: "",
    partyPhone: "",
    amount: "",
    amountReceived: "",
    purpose: "general",
    donationDate: new Date().toISOString().split("T")[0],
    status: "pending",
    notes: "",
    receiptNumber: "",
    anonymous: false,
    studentName: "",
    studentClass: "",
    purposeDetails: "",
    trustName: "",
    trustRegistrationNumber: ""
  };
  const [formData, setFormData] = useState(emptyForm);
  useEffect(() => {
    if (editingDonation) {
      setFormData({
        ...emptyForm,
        ...editingDonation,
        donationDate: editingDonation.donationDate ? new Date(editingDonation.donationDate).toISOString().split("T")[0] : emptyForm.donationDate,
        amount: editingDonation.amount || "",
        amountReceived: editingDonation.amountReceived || ""
      });
    } else {
      setFormData(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDonation, isOpen]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };
  const handleSubmit = e => {
    e.preventDefault();
    onSave(formData);
  };
  if (!isOpen) return null;
  const isGiven = formData.direction === "given";
  const isTrust = formData.partyType === "trust";
  const headerLabel = isGiven ? "School Trust/NGO Contribution" : "Incoming Donation";
  const partyLabel = isGiven ? "Recipient Trust / NGO Name *" : "Donor Name *";
  const partyTypeLabel = isGiven ? "Recipient Type" : "Donor Type";
  const totalAmt = parseFloat(formData.amount) || 0;
  const receivedAmt = parseFloat(formData.amountReceived) || 0;
  const balanceAmt = Math.max(totalAmt - receivedAmt, 0);
  const showBalance = formData.amountReceived !== "" && totalAmt > 0;
  return <div className="fixed inset-0 bg-[#0D47A1]/60 backdrop-blur-sm flex items-center justify-center z-[200] animate-fade-in-scale p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-[650px] max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-fade-in-scale" onClick={e => e.stopPropagation()}>
        {/* Header - Dark Blue */}
        <div className="bg-[#0D47A1] px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <div className="text-white font-bold text-lg flex items-center gap-2">
              <Gift size={20} className="text-[#2196F3]" />
              <span>{editingDonation ? "Edit Record" : "New Donation Record"}</span>
            </div>
            <div className="text-white/80 text-xs mt-1">{headerLabel}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-[#2196F3] hover:text-white transition-all duration-200">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {formError && <div className="mb-4 rounded-xl px-4 py-3 text-sm font-bold bg-[#E3F2FD] text-[#0D47A1] flex items-start gap-2 shadow-sm">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{formError}</span>
            </div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Direction Toggle */}
            <div className="bg-white rounded-xl p-1.5 flex gap-1.5 shadow-sm">
              <button type="button" onClick={() => setFormData(p => ({
              ...p,
              direction: "received"
            }))} className={`flex-1 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${!isGiven ? "bg-[#2196F3] text-white shadow-md" : "text-[#0D47A1] hover:bg-[#E3F2FD]"}`}>
                <ArrowDownCircle size={16} /> Trust/Donor <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /> School
              </button>
              <button type="button" onClick={() => setFormData(p => ({
              ...p,
              direction: "given"
            }))} className={`flex-1 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all ${isGiven ? "bg-[#0D47A1] text-white shadow-md" : "text-[#0D47A1] hover:bg-[#E3F2FD]"}`}>
                <ArrowUpCircle size={16} /> School <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /> Trust/NGO
              </button>
            </div>

            {/* Party Information */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h4 className="text-xs font-bold text-[#0D47A1] uppercase tracking-wide mb-3 flex items-center gap-2">
                <Users size={14} /> {isGiven ? "Recipient Information" : "Donor Information"}
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">{partyLabel}</label>
                  <input type="text" name="partyName" value={formData.partyName} onChange={handleChange} required className="w-full bg-[#E3F2FD] rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder={isGiven ? "e.g. Green Earth Foundation" : "e.g. Ramesh Kumar / ABC Foundation"} />
                </div>
                <div>
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">{partyTypeLabel}</label>
                  <select name="partyType" value={formData.partyType} onChange={handleChange} className="w-full bg-[#E3F2FD] rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all">
                    <option value="individual"><User className="inline w-[1em] h-[1em] align-[-0.125em]" /> Individual</option>
                    <option value="billionaire"><Gem className="inline w-[1em] h-[1em] align-[-0.125em]" /> Philanthropist</option>
                    <option value="corporate"><Building2 className="inline w-[1em] h-[1em] align-[-0.125em]" /> Corporate</option>
                    <option value="trust"><Landmark className="inline w-[1em] h-[1em] align-[-0.125em]" /> Trust/NGO</option>
                    <option value="anonymous"><Lock className="inline w-[1em] h-[1em] align-[-0.125em]" /> Anonymous</option>
                    <option value="alumni"><GraduationCap className="inline w-[1em] h-[1em] align-[-0.125em]" /> Alumni</option>
                    <option value="government"><Landmark className="inline w-[1em] h-[1em] align-[-0.125em]" /> Government</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Date</label>
                  <input type="date" name="donationDate" value={formData.donationDate} onChange={handleChange} className="w-full bg-[#E3F2FD] rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" />
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Email</label>
                  <input type="email" name="partyEmail" value={formData.partyEmail} onChange={handleChange} className="w-full bg-[#E3F2FD] rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="contact@example.com" />
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Phone</label>
                  <input type="text" name="partyPhone" value={formData.partyPhone} onChange={handleChange} className="w-full bg-[#E3F2FD] rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="+91 98765 43210" />
                </div>
              </div>
            </div>

            {/* Trust Details */}
            {isTrust && <div className="bg-white rounded-xl p-4 shadow-sm">
                <h4 className="text-xs font-bold text-[#0D47A1] uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Building size={14} /> Trust/NGO Details
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Trust/NGO Legal Name</label>
                    <input type="text" name="trustName" value={formData.trustName} onChange={handleChange} className="w-full bg-[#E3F2FD] rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="e.g. ABC Foundation Trust" />
                  </div>
                  <div>
                    <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Registration Number</label>
                    <input type="text" name="trustRegistrationNumber" value={formData.trustRegistrationNumber} onChange={handleChange} className="w-full bg-[#E3F2FD] rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="e.g. TR-2024-001" />
                  </div>
                </div>
              </div>}

            {/* Amount & Purpose */}
            <div className="bg-[#E3F2FD] rounded-xl p-4">
              <h4 className="text-xs font-bold text-[#0D47A1] uppercase tracking-wide mb-3 flex items-center gap-2">
                <Gift size={14} /> {isGiven ? "Contribution Details" : "Donation Details"}
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Amount (₹) *</label>
                  <input type="number" name="amount" value={formData.amount} onChange={handleChange} required min="0" step="0.01" className="w-full bg-white rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="100000" />
                </div>
                <div>
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">
                    Amount Received (₹) <span className="text-[#0D47A1] normal-case">(optional)</span>
                  </label>
                  <input type="number" name="amountReceived" value={formData.amountReceived} onChange={handleChange} min="0" step="0.01" className="w-full bg-white rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="e.g. 50000" />
                </div>
                {showBalance && <div className="col-span-2 rounded-lg px-3 py-2 flex items-center justify-between bg-white border border-dashed border-[#0D47A1]">
                    <span className="text-[11px] font-bold text-[#0D47A1] uppercase tracking-wide">Balance Remaining</span>
                    <span className={`text-sm font-extrabold ${balanceAmt > 0 ? "text-[#0D47A1]" : "text-[#2196F3]"}`}>
                      ₹{balanceAmt.toLocaleString()} {balanceAmt === 0 ? "— fully paid" : "still due"}
                    </span>
                  </div>}
                <div>
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Purpose</label>
                  <select name="purpose" value={formData.purpose} onChange={handleChange} className="w-full bg-white rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all">
                    <option value="infrastructure"><HardHat className="inline w-[1em] h-[1em] align-[-0.125em]" /> Infrastructure</option>
                    <option value="building"><Landmark className="inline w-[1em] h-[1em] align-[-0.125em]" /> Building Construction</option>
                    <option value="scholarship"><GraduationCap className="inline w-[1em] h-[1em] align-[-0.125em]" /> Scholarship</option>
                    <option value="equipment"><Monitor className="inline w-[1em] h-[1em] align-[-0.125em]" /> Equipment</option>
                    <option value="general"><Package className="inline w-[1em] h-[1em] align-[-0.125em]" /> General</option>
                    <option value="event"><Tent className="inline w-[1em] h-[1em] align-[-0.125em]" /> Event</option>
                    <option value="maintenance"><Wrench className="inline w-[1em] h-[1em] align-[-0.125em]" /> Maintenance</option>
                    <option value="library"><BookOpen className="inline w-[1em] h-[1em] align-[-0.125em]" /> Library</option>
                    <option value="sports"><CircleDot className="inline w-[1em] h-[1em] align-[-0.125em]" /> Sports</option>
                    <option value="technology"><Monitor className="inline w-[1em] h-[1em] align-[-0.125em]" /> Technology</option>
                    <option value="staff"><User className="inline w-[1em] h-[1em] align-[-0.125em]" /><School className="inline w-[1em] h-[1em] align-[-0.125em]" /> Staff Welfare</option>
                    <option value="transport"><Bus className="inline w-[1em] h-[1em] align-[-0.125em]" /> Transport</option>
                    <option value="disaster_relief">🆘 Disaster Relief</option>
                    <option value="community"><Handshake className="inline w-[1em] h-[1em] align-[-0.125em]" /> Community Support</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">
                    {isGiven ? "How the contribution will be used" : "Purpose Description / Use of Funds"}
                  </label>
                  <textarea name="purposeDetails" value={formData.purposeDetails} onChange={handleChange} rows={2} className="w-full bg-white rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all resize-none" placeholder={isGiven ? "e.g. Supporting flood relief drive for 200 families" : "e.g. Building new computer lab"} />
                </div>
                <div>
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Status</label>
                  <select name="status" value={formData.status} onChange={handleChange} className="w-full bg-white rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all">
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Receipt Number <span className="text-[#0D47A1] normal-case">(optional — auto-generated)</span></label>
                  <input type="text" name="receiptNumber" value={formData.receiptNumber} onChange={handleChange} className="w-full bg-white rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="Leave blank to auto-generate" />
                </div>
                {!isGiven && <div className="col-span-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" name="anonymous" checked={formData.anonymous} onChange={handleChange} className="accent-[#0D47A1] w-4 h-4" />
                      <span className="text-sm text-[#0D47A1] font-medium">Anonymous Donation</span>
                    </label>
                  </div>}
              </div>
            </div>

            {/* Student Association */}
            {!isGiven && <div className="bg-white rounded-xl p-4 shadow-sm">
                <h4 className="text-xs font-bold text-[#0D47A1] uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Users size={14} /> Student Association (Optional)
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Student Name</label>
                    <input type="text" name="studentName" value={formData.studentName} onChange={handleChange} className="w-full bg-[#E3F2FD] rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="Student name (if specific)" />
                  </div>
                  <div>
                    <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Class</label>
                    <input type="text" name="studentClass" value={formData.studentClass} onChange={handleChange} className="w-full bg-[#E3F2FD] rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="e.g. Class 10" />
                  </div>
                </div>
              </div>}

            {/* Notes */}
            <div>
              <label className="text-[11px] text-[#0D47A1] font-medium block mb-1">Notes / Remarks</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} className="w-full bg-white rounded-lg px-3 py-2 text-sm text-[#0D47A1] outline-none focus:ring-2 focus:ring-[#0D47A1] transition-all resize-none shadow-sm" placeholder="Any additional notes..." />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} disabled={saving} className="flex-1 py-2.5 rounded-xl text-[#0D47A1] font-bold hover:bg-[#E3F2FD] transition-all duration-200 text-sm disabled:opacity-50">
                Cancel
              </button>
              <button type="submit" disabled={saving} className="flex-1 py-2.5 text-white font-bold rounded-xl shadow-lg transition-all duration-200 text-sm disabled:opacity-60 bg-[#0D47A1] hover:bg-[#0D47A1]/90 shadow-[#0D47A1]/25">
                {saving ? "Saving..." : editingDonation ? "Update Record" : "Save Record"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}

// ─── DETAILS MODAL ────────────────────────────────────────────────────────────

function DonationDetailsModal({
  donation,
  onClose
}) {
  if (!donation) return null;
  const isGiven = donation.direction === "given";
  return <div className="fixed inset-0 bg-[#0D47A1]/60 backdrop-blur-sm flex items-center justify-center z-[200] animate-fade-in-scale p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-[550px] max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-fade-in-scale" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0 bg-[#2196F3]">
          <div>
            <div className="text-white font-bold text-lg flex items-center gap-2">
              <Heart size={20} className="text-white" />
              <span>Record Details</span>
            </div>
            <div className="text-white/80 text-xs mt-1">Reference: {donation.receiptNumber || "N/A"}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-[#0D47A1] hover:text-white transition-all duration-200">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            <div className="rounded-2xl p-6 text-center bg-[#E3F2FD]">
              <div className="text-sm text-[#0D47A1] font-medium">{isGiven ? "Contribution Given by School" : "Donation Received"}</div>
              <div className="text-4xl font-extrabold mt-2 text-[#0D47A1]">₹{donation.amount?.toLocaleString() || 0}</div>
              <div className="mt-2 flex justify-center gap-2 flex-wrap">
                <DirectionBadge direction={donation.direction} />
                <PurposeBadge purpose={donation.purpose} />
                <StatusBadge status={donation.status} />
              </div>
              {donation.amount > 0 && <div className="mt-4 pt-4 border-t border-[#90CAF9] grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold">Confirmed</div>
                    <div className="text-sm font-extrabold text-[#0D47A1] mt-1">₹{(donation.amount || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold">Received</div>
                    <div className="text-sm font-extrabold text-[#2196F3] mt-1">₹{(donation.amountReceived || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold">Balance</div>
                    <div className="text-sm font-extrabold text-[#0D47A1] mt-1">
                      ₹{Math.max((donation.amount || 0) - (donation.amountReceived || 0), 0).toLocaleString()}
                    </div>
                  </div>
                </div>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold">{isGiven ? "Recipient" : "Donor Name"}</div>
                <div className="text-sm font-bold text-[#0D47A1] mt-1">{donation.anonymous ? "Anonymous Donor" : donation.partyName || "N/A"}</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold">{isGiven ? "Recipient Type" : "Donor Type"}</div>
                <div className="text-sm font-bold text-[#0D47A1] mt-1"><PartyTypeBadge type={donation.partyType} /></div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold">Date</div>
                <div className="text-sm font-bold text-[#0D47A1] mt-1">
                  {donation.donationDate ? new Date(donation.donationDate).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                }) : "N/A"}
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold">Receipt</div>
                <div className="text-sm font-bold text-[#0D47A1] mt-1">{donation.receiptNumber || "Not issued"}</div>
              </div>
            </div>

            {donation.partyType === "trust" && (donation.trustName || donation.trustRegistrationNumber) && <div className="bg-[#E3F2FD] rounded-xl p-4">
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold mb-2">Trust/NGO Details</div>
                <div className="space-y-1 text-sm">
                  {donation.trustName && <div><span className="text-[#0D47A1]">Trust Name:</span> <span className="font-medium text-[#0D47A1]">{donation.trustName}</span></div>}
                  {donation.trustRegistrationNumber && <div><span className="text-[#0D47A1]">Registration:</span> <span className="font-medium text-[#0D47A1]">{donation.trustRegistrationNumber}</span></div>}
                </div>
              </div>}

            {donation.purposeDetails && <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold mb-2">Purpose Description</div>
                <div className="text-sm text-[#0D47A1]">{donation.purposeDetails}</div>
              </div>}

            {(donation.partyEmail || donation.partyPhone) && <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold mb-2">Contact Information</div>
                <div className="space-y-1 text-sm">
                  {donation.partyEmail && <div><span className="text-[#0D47A1]">Email:</span> <span className="font-medium text-[#0D47A1]">{donation.partyEmail}</span></div>}
                  {donation.partyPhone && <div><span className="text-[#0D47A1]">Phone:</span> <span className="font-medium text-[#0D47A1]">{donation.partyPhone}</span></div>}
                </div>
              </div>}

            {!isGiven && (donation.studentName || donation.studentClass) && <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold mb-2">Associated Student</div>
                <div className="flex gap-4 text-sm">
                  {donation.studentName && <div><span className="text-[#0D47A1]">Name:</span> <span className="font-medium text-[#0D47A1]">{donation.studentName}</span></div>}
                  {donation.studentClass && <div><span className="text-[#0D47A1]">Class:</span> <span className="font-medium text-[#0D47A1]">{donation.studentClass}</span></div>}
                </div>
              </div>}

            {donation.notes && <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold mb-2">Notes</div>
                <div className="text-sm text-[#0D47A1]">{donation.notes}</div>
              </div>}

            <button onClick={onClose} className="w-full py-2.5 rounded-xl text-sm font-bold transition-all duration-200 bg-[#0D47A1] text-white hover:bg-[#0D47A1]/90">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>;
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function DonationManagement() {
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL || "https://schoolcrm-fzcv.onrender.com";
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterDirection, setFilterDirection] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPurpose, setFilterPurpose] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editingDonation, setEditingDonation] = useState(null);
  const [viewingDonation, setViewingDonation] = useState(null);
  const [notice, setNotice] = useState("");
  const [noticeType, setNoticeType] = useState("success");
  const [viewMode, setViewMode] = useState("list");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [connectionOk, setConnectionOk] = useState(null);
  useEffect(() => {
    fetchDonations();
  }, []);
  async function readServerError(res) {
    try {
      const data = await res.json();
      return data.error || data.message || `Server responded with status ${res.status}`;
    } catch {
      return `Server responded with status ${res.status}`;
    }
  }
  async function fetchDonations() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/donations`);
      if (!res.ok) {
        const msg = await readServerError(res);
        throw new Error(msg);
      }
      const data = await res.json();
      setDonations(Array.isArray(data) ? data : []);
      setConnectionOk(true);
      setNotice("");
    } catch (err) {
      setConnectionOk(false);
      setDonations([]);
      setNoticeType("error");
      setNotice(` Could not reach the backend at ${API_BASE}. (${err.message})`);
    }
    setLoading(false);
  }
  async function handleSaveDonation(formData) {
    setSaving(true);
    setFormError("");
    const payload = {
      ...formData,
      amount: parseFloat(formData.amount) || 0,
      amountReceived: formData.amountReceived === "" ? 0 : parseFloat(formData.amountReceived) || 0
    };
    if (!payload.receiptNumber) delete payload.receiptNumber;
    try {
      const url = editingDonation ? `${API_BASE}/api/donations/${editingDonation._id}` : `${API_BASE}/api/donations`;
      const method = editingDonation ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const msg = await readServerError(res);
        throw new Error(msg);
      }
      setNoticeType("success");
      setNotice(editingDonation ? "Record updated in the database!" : "Record saved to the database!");
      setShowForm(false);
      setEditingDonation(null);
      setConnectionOk(true);
      await fetchDonations();
    } catch (err) {
      setFormError(`Save failed: ${err.message}. This record was NOT saved to the database.`);
    } finally {
      setSaving(false);
    }
  }
  async function handleDeleteDonation(id) {
    if (!window.confirm("Are you sure you want to delete this record? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API_BASE}/api/donations/${id}`, {
        method: "DELETE"
      });
      if (!res.ok) {
        const msg = await readServerError(res);
        throw new Error(msg);
      }
      setNoticeType("success");
      setNotice(" Record deleted from the database!");
      await fetchDonations();
    } catch (err) {
      setNoticeType("error");
      setNotice(` Delete failed: ${err.message}.`);
    }
  }
  const filteredDonations = donations.filter(d => {
    const matchSearch = d.partyName?.toLowerCase().includes(search.toLowerCase()) || d.receiptNumber?.toLowerCase().includes(search.toLowerCase()) || d.studentName?.toLowerCase().includes(search.toLowerCase()) || d.purposeDetails?.toLowerCase().includes(search.toLowerCase()) || d.trustName?.toLowerCase().includes(search.toLowerCase());
    const matchDirection = filterDirection === "all" || d.direction === filterDirection;
    const matchType = filterType === "all" || d.partyType === filterType;
    const matchStatus = filterStatus === "all" || d.status === filterStatus;
    const matchPurpose = filterPurpose === "all" || d.purpose === filterPurpose;
    return matchSearch && matchDirection && matchType && matchStatus && matchPurpose;
  });
  const receivedDonations = donations.filter(d => d.direction !== "given");
  const givenDonations = donations.filter(d => d.direction === "given");
  const totalReceived = receivedDonations.reduce((sum, d) => sum + (d.amount || 0), 0);
  const totalGiven = givenDonations.reduce((sum, d) => sum + (d.amount || 0), 0);
  const netBalance = totalReceived - totalGiven;
  const completedReceivedTotal = receivedDonations.filter(d => d.status === "completed" || d.status === "received").reduce((sum, d) => sum + (d.amount || 0), 0);
  const pendingTotal = donations.filter(d => d.status === "pending").reduce((sum, d) => sum + (d.amount || 0), 0);
  const totalDonors = new Set(receivedDonations.map(d => d.partyName)).size;
  const trustTotal = receivedDonations.filter(d => d.partyType === "trust").reduce((sum, d) => sum + (d.amount || 0), 0);
  const purposeCounts = {};
  donations.forEach(d => {
    purposeCounts[d.purpose] = (purposeCounts[d.purpose] || 0) + (d.amount || 0);
  });
  const sortedPurposes = Object.entries(purposeCounts).sort((a, b) => b[1] - a[1]);
  useEffect(() => {
    if (notice && noticeType === "success") {
      const timer = setTimeout(() => setNotice(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [notice, noticeType]);
  const purposeIcons = {
    infrastructure: "",
    building: "",
    scholarship: <GraduationCap className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    equipment: <Monitor className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    general: <Package className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    event: <Tent className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    maintenance: <Wrench className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    library: <BookOpen className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    sports: <CircleDot className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    technology: "",
    staff: "",
    transport: <Bus className="inline w-[1em] h-[1em] align-[-0.125em]" />,
    disaster_relief: "🆘",
    community: <Handshake className="inline w-[1em] h-[1em] align-[-0.125em]" />
  };
  return <div className="animate-fade-in-up bg-white min-h-screen p-6">
      {/* ===== HEADER WITH BACK BUTTON ===== */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Back Button */}
            <button onClick={() => navigate("/admin")} className="flex items-center gap-2 px-4 py-2.5 bg-[#0D47A1] text-white hover:bg-[#2196F3] transition-all duration-200 shadow-sm hover:shadow-md rounded-xl group" title="Back to Admin Dashboard">
              <ArrowLeft size={18} className="text-white group-hover:text-white transition-colors" />
              <span className="text-sm font-bold text-white group-hover:text-white">Back</span>
            </button>

            <div>
              <h2 className="text-2xl font-extrabold text-[#0D47A1] flex items-center gap-3">
                <Heart className="text-[#2196F3]" size={28} />
                Donation Management
                <span className="hidden md:inline text-sm text-[#0D47A1] font-medium">/ Received & Given</span>
              </h2>
              <p className="text-sm text-[#0D47A1] mt-1">
                Track donations received from trusts, philanthropists, corporates & individuals — and contributions the school gives to other trusts/NGOs
              </p>
            </div>
          </div>

          {/* Add Record Button */}
          <button onClick={() => {
          setEditingDonation(null);
          setFormError("");
          setShowForm(true);
        }} className="inline-flex items-center gap-2 bg-[#2196F3] hover:bg-[#2196F3]/90 text-white text-sm font-bold px-5 py-3 rounded-xl shadow-lg shadow-[#2196F3]/40 transition-all duration-200 hover:-translate-y-0.5">
            <Plus size={18} />
            Add Record
          </button>
        </div>
      </div>

      {/* Notice */}
      {notice && <div className={`mb-4 rounded-xl px-5 py-3 text-sm font-bold animate-fade-in-up shadow-sm flex items-start gap-2 ${noticeType === "error" ? "bg-[#E3F2FD] text-[#0D47A1]" : "bg-[#2196F3] text-white"}`}>
          <span>{noticeType === "error" ? "" : <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />}</span> <span>{notice}</span>
        </div>}

      {/* Direction Tabs */}
      <div className="flex gap-2 mb-6 bg-[#E3F2FD] p-1.5 rounded-2xl w-fit">
        {[{
        key: "all",
        label: "All Records",
        icon: <ArrowRightLeft size={15} />
      }, {
        key: "received",
        label: "Received by School",
        icon: <ArrowDownCircle size={15} />
      }, {
        key: "given",
        label: "Given by School",
        icon: <ArrowUpCircle size={15} />
      }].map(tab => <button key={tab.key} onClick={() => setFilterDirection(tab.key)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${filterDirection === tab.key ? "bg-[#0D47A1] text-white shadow-md" : "text-[#0D47A1] hover:bg-[#90CAF9]"}`}>
            {tab.icon} {tab.label}
          </button>)}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
        {[{
        icon: <ArrowDownCircle size={18} className="text-[#2196F3]" />,
        value: totalReceived,
        label: "Total Received",
        prefix: "₹",
        bg: "bg-white",
        text: "text-[#0D47A1]"
      }, {
        icon: <ArrowUpCircle size={18} className="text-[#2196F3]" />,
        value: totalGiven,
        label: "Total Given by School",
        prefix: "₹",
        bg: "bg-white",
        text: "text-[#0D47A1]"
      }, {
        icon: <Building size={18} className="text-[#2196F3]" />,
        value: trustTotal,
        label: "From Trust/NGO",
        prefix: "₹",
        bg: "bg-white",
        text: "text-[#0D47A1]"
      }, {
        icon: <CheckCircle size={18} className="text-[#2196F3]" />,
        value: completedReceivedTotal,
        label: "Completed (In)",
        prefix: "₹",
        bg: "bg-white",
        text: "text-[#0D47A1]"
      }, {
        icon: <Clock size={18} className="text-[#2196F3]" />,
        value: pendingTotal,
        label: "Pending",
        prefix: "₹",
        bg: "bg-white",
        text: "text-[#0D47A1]"
      }, {
        icon: <Users size={18} className="text-[#2196F3]" />,
        value: totalDonors,
        label: "Total Donors",
        bg: "bg-white",
        text: "text-[#0D47A1]"
      }].map((stat, i) => <div key={i} className={`${stat.bg} rounded-2xl p-4 shadow-sm hover:shadow-md transition-all duration-200`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E3F2FD] flex items-center justify-center">{stat.icon}</div>
              <div>
                <div className={`text-lg font-extrabold ${stat.text}`}><AnimatedCounter value={stat.value} prefix={stat.prefix || ""} /></div>
                <div className="text-[10px] text-[#0D47A1] uppercase tracking-wide font-bold">{stat.label}</div>
              </div>
            </div>
          </div>)}
      </div>

      {/* Net Balance Banner */}
      <div className="rounded-2xl p-5 mb-6 bg-[#0D47A1] flex items-center justify-between flex-wrap gap-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center">
            <ArrowRightLeft size={20} className="text-[#0D47A1]" />
          </div>
          <div>
            <div className="text-xs text-[#90CAF9] uppercase tracking-wide font-bold">Net Balance (Received − Given)</div>
            <div className="text-xl font-extrabold text-white">
              ₹{Math.abs(netBalance).toLocaleString()} {netBalance >= 0 ? "surplus" : "given out more"}
            </div>
          </div>
        </div>
        <div className="text-xs text-[#90CAF9] max-w-xs text-right">
          Money in from trusts, philanthropists & corporates, minus what the school has contributed onward to other trusts/NGOs.
        </div>
      </div>

      {/* Purpose Distribution */}
      {sortedPurposes.length > 0 && <div className="bg-[#E3F2FD] rounded-2xl p-5 mb-6 shadow-sm">
          <h4 className="text-xs font-bold text-[#0D47A1] uppercase tracking-wide mb-3">Purpose Distribution (All Records)</h4>
          <div className="flex flex-wrap gap-3">
            {sortedPurposes.map(([purpose, amount]) => <div key={purpose} className="bg-white rounded-xl px-4 py-2 shadow-sm flex items-center gap-2">
                <span className="text-lg">{purposeIcons[purpose] || <Package className="inline w-[1em] h-[1em] align-[-0.125em]" />}</span>
                <PurposeBadge purpose={purpose} />
                <span className="ml-1 text-sm font-bold text-[#0D47A1]">₹{amount.toLocaleString()}</span>
              </div>)}
          </div>
        </div>}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D47A1]" />
          <input value={search} onChange={e => setSearch(e.target.value)} className="w-full bg-white rounded-xl pl-10 pr-4 py-2.5 text-sm text-[#0D47A1] outline-none placeholder-[#0D47A1]/60 shadow-sm focus:ring-2 focus:ring-[#0D47A1] transition-all" placeholder="Search by name, receipt, purpose..." />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="bg-white rounded-xl px-4 py-2.5 text-sm text-[#0D47A1] outline-none cursor-pointer shadow-sm focus:ring-2 focus:ring-[#0D47A1]">
          <option value="all">All Types</option>
          <option value="individual">Individual</option>
          <option value="billionaire">Philanthropist</option>
          <option value="corporate">Corporate</option>
          <option value="trust">Trust/NGO</option>
          <option value="anonymous">Anonymous</option>
          <option value="alumni">Alumni</option>
          <option value="government">Government</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-white rounded-xl px-4 py-2.5 text-sm text-[#0D47A1] outline-none cursor-pointer shadow-sm focus:ring-2 focus:ring-[#0D47A1]">
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="completed">Completed</option>
          <option value="received">Received</option>
          <option value="approved">Approved</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={filterPurpose} onChange={e => setFilterPurpose(e.target.value)} className="bg-white rounded-xl px-4 py-2.5 text-sm text-[#0D47A1] outline-none cursor-pointer shadow-sm focus:ring-2 focus:ring-[#0D47A1]">
          <option value="all">All Purposes</option>
          <option value="infrastructure">Infrastructure</option>
          <option value="building">Building</option>
          <option value="scholarship">Scholarship</option>
          <option value="equipment">Equipment</option>
          <option value="general">General</option>
          <option value="event">Event</option>
          <option value="maintenance">Maintenance</option>
          <option value="library">Library</option>
          <option value="sports">Sports</option>
          <option value="technology">Technology</option>
          <option value="staff">Staff Welfare</option>
          <option value="transport">Transport</option>
          <option value="disaster_relief">Disaster Relief</option>
          <option value="community">Community Support</option>
        </select>
        <button onClick={() => {
        setSearch("");
        setFilterType("all");
        setFilterStatus("all");
        setFilterPurpose("all");
        setFilterDirection("all");
      }} className="px-4 py-2.5 text-sm font-bold text-white bg-[#0D47A1] hover:bg-[#2196F3] transition-all duration-200 rounded-xl">
          Clear Filters
        </button>
        <button onClick={() => setViewMode(viewMode === "list" ? "grid" : "list")} className="px-4 py-2.5 text-sm font-bold text-white bg-[#0D47A1] hover:bg-[#2196F3] transition-all duration-200 rounded-xl">
          {viewMode === "list" ? "Grid" : "List"}
        </button>
        <button onClick={fetchDonations} className="px-4 py-2.5 text-sm font-bold text-white bg-[#0D47A1] hover:bg-[#2196F3] transition-all duration-200 rounded-xl">
          <RotateCw className="inline w-[1em] h-[1em] align-[-0.125em]" /> Refresh
        </button>
      </div>

      {/* Loading / Empty / Content */}
      {loading ? <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#90CAF9] border-t-[#0D47A1] rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-[#0D47A1] font-bold text-lg animate-pulse">Loading Records...</div>
          </div>
        </div> : filteredDonations.length === 0 ? <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
          <div className="text-6xl mb-4 animate-float"><GiftIcon className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
          <div className="text-[#0D47A1] font-semibold text-lg">
            {connectionOk === false ? "Can't load records — backend not reachable" : "No donations found"}
          </div>
          <div className="text-sm text-[#0D47A1] mt-1">
            {connectionOk === false ? "Fix the connection above, then click Refresh" : 'Click "Add Record" to save a new donation or contribution'}
          </div>
        </div> : viewMode === "grid" ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDonations.map(d => <div key={d._id} className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-bold text-[#0D47A1] text-sm">{d.anonymous ? "Anonymous Donor" : d.partyName}</div>
                  <PartyTypeBadge type={d.partyType} />
                </div>
                <StatusBadge status={d.status} />
              </div>
              <div className="mb-2"><DirectionBadge direction={d.direction} /></div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl font-extrabold text-[#0D47A1]">₹{d.amount?.toLocaleString() || 0}</span>
                <PurposeBadge purpose={d.purpose} />
              </div>
              {d.amountReceived > 0 && d.amountReceived < (d.amount || 0) && <div className="text-[11px] text-[#0D47A1] mb-2">
                  Received ₹{d.amountReceived.toLocaleString()} · <span className="font-bold">Balance ₹{((d.amount || 0) - d.amountReceived).toLocaleString()} due</span>
                </div>}
              {d.purposeDetails && <p className="text-xs text-[#0D47A1] mt-2 line-clamp-2">{d.purposeDetails}</p>}
              {d.partyType === "trust" && d.trustName && <div className="mt-2 text-[10px] text-white bg-[#0D47A1] px-2 py-1 rounded-lg inline-block"><Landmark className="inline w-[1em] h-[1em] align-[-0.125em]" /> {d.trustName}</div>}
              <div className="mt-3 flex justify-between items-center">
                <span className="text-[10px] text-[#0D47A1]">{d.donationDate ? new Date(d.donationDate).toLocaleDateString("en-IN") : "N/A"}</span>
                <div className="flex gap-2">
                  <button onClick={() => setViewingDonation(d)} className="text-[#0D47A1] hover:text-white hover:bg-[#0D47A1] p-1 rounded-lg transition-colors"><Eye size={16} /></button>
                  <button onClick={() => {
              setEditingDonation(d);
              setFormError("");
              setShowForm(true);
            }} className="text-[#0D47A1] hover:text-white hover:bg-[#0D47A1] p-1 rounded-lg transition-colors"><Edit size={16} /></button>
                  <button onClick={() => handleDeleteDonation(d._id)} className="text-[#0D47A1] hover:text-white hover:bg-[#0D47A1] p-1 rounded-lg transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>)}
        </div> : <GlassCard>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0D47A1]">
                  {["#", "Party", "Direction", "Type", "Purpose", "Amount", "Status", "Date", "Actions"].map(h => <th key={h} className="text-[11px] text-white font-bold uppercase tracking-wide px-5 py-4 text-left whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filteredDonations.map((d, i) => <tr key={d._id || i} className="hover:bg-[#E3F2FD] transition-colors duration-200">
                    <td className="px-5 py-4 text-sm text-[#0D47A1] font-medium">{i + 1}</td>
                    <td className="px-5 py-4">
                      <div>
                        <div className="text-sm font-bold text-[#0D47A1]">{d.anonymous ? "Anonymous" : d.partyName || "N/A"}</div>
                        {d.partyType === "trust" && d.trustName && <div className="text-[10px] text-[#0D47A1] mt-0.5"><Landmark className="inline w-[1em] h-[1em] align-[-0.125em]" /> {d.trustName}</div>}
                        {d.receiptNumber && <div className="text-[10px] text-[#0D47A1] mt-0.5">Ref: {d.receiptNumber}</div>}
                      </div>
                    </td>
                    <td className="px-5 py-4"><DirectionBadge direction={d.direction} /></td>
                    <td className="px-5 py-4"><PartyTypeBadge type={d.partyType} /></td>
                    <td className="px-5 py-4">
                      <PurposeBadge purpose={d.purpose} />
                      {d.purposeDetails && <div className="text-[10px] text-[#0D47A1] mt-1 truncate max-w-[150px]">{d.purposeDetails}</div>}
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-base font-bold text-[#0D47A1]">₹{d.amount?.toLocaleString() || 0}</span>
                      {d.amountReceived > 0 && d.amountReceived < (d.amount || 0) && <div className="text-[10px] text-[#0D47A1] mt-0.5">
                          Recd ₹{d.amountReceived.toLocaleString()} · <span className="font-bold">Bal ₹{((d.amount || 0) - d.amountReceived).toLocaleString()}</span>
                        </div>}
                    </td>
                    <td className="px-5 py-4"><StatusBadge status={d.status} /></td>
                    <td className="px-5 py-4 text-sm text-[#0D47A1]">{d.donationDate ? new Date(d.donationDate).toLocaleDateString("en-IN") : "N/A"}</td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2">
                        <button onClick={() => setViewingDonation(d)} className="text-[#0D47A1] hover:text-white hover:bg-[#0D47A1] p-1 rounded-lg transition-colors" title="View Details"><Eye size={16} /></button>
                        <button onClick={() => {
                    setEditingDonation(d);
                    setFormError("");
                    setShowForm(true);
                  }} className="text-[#0D47A1] hover:text-white hover:bg-[#0D47A1] p-1 rounded-lg transition-colors" title="Edit"><Edit size={16} /></button>
                        <button onClick={() => handleDeleteDonation(d._id)} className="text-[#0D47A1] hover:text-white hover:bg-[#0D47A1] p-1 rounded-lg transition-colors" title="Delete"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </GlassCard>}

      <DonationFormModal isOpen={showForm} onClose={() => {
      setShowForm(false);
      setEditingDonation(null);
      setFormError("");
    }} onSave={handleSaveDonation} editingDonation={editingDonation} saving={saving} formError={formError} />

      <DonationDetailsModal donation={viewingDonation} onClose={() => setViewingDonation(null)} />

      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInScale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-fade-in-up { animation: fadeInUp 0.5s ease-out forwards; }
        .animate-fade-in-scale { animation: fadeInScale 0.3s ease-out forwards; }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .animate-spin { animation: spin 1s linear infinite; }
        .glass-panel { background: rgba(255, 255, 255, 0.9); backdrop-filter: blur(16px); }
        .card-hover { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .card-hover:hover { transform: translateY(-4px); box-shadow: 0 20px 40px -12px rgba(13, 71, 161, 0.15); }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>;
}